/**
 * Prospects API Service
 * Handles fetching prospect data from the backend
 */

import { get, put, post, del, patch, getBlob, ApiError } from "./api";
import type {
  PaginatedResponse,
  ProspectResponse,
  CreateProspectRequest,
  ProspectDefaultTag,
  PartialPositionPlayerMetrics,
  PartialPitcherMetrics,
  MetricEntryResponse,
  GetMetricEntriesParams,
  MergeProspectsRequest,
  MergeProspectsResponse,
  MergeErrorResponse,
  CheckImportDuplicatesRequest,
  CheckImportDuplicatesResponse,
  DuplicateCandidate,
  DuplicateCountResponse,
  DuplicateSweepResponse,
  DismissedDuplicate,
  BulkMergeRequest,
  BulkMergeResponse,
  MergeAllJob,
  FilteredProspectsResponse,
} from "../types/api";
import type { DefaultTagDefinition, TagFilters, CreateTagDefinitionRequest } from "../types/tags";
import {
  GRAD_YEAR_FILTER_KEY,
  JUCO_YEAR_FILTER_KEY,
  PLAYER_LEVEL_FILTER_KEY,
} from "../types/tags";
import type { SortDirection } from "../types/sort";
import type { JucoYear } from "../utils/roster-class";
import { isJucoPlayerLevel } from "../constants/playerLevels";

export type GetProspectsParams = {
  teamId?: string;
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  tagFilters?: TagFilters;
  isFavorite?: boolean;
  /**
   * Column to sort by: "name", "position", "jerseyNumber", "gradYear", or a tag
   * category as `tag_<NameWithoutSpaces>`. Omit for the view's default order.
   *
   * Sorting is server-side because the list is paginated server-side — sorting
   * only the loaded page would reorder 15 rows out of the whole result set.
   */
  sortBy?: string;
  sortDirection?: SortDirection;
};

/**
 * The query param each reserved (non-tag) filter key is sent as.
 *
 * Every one of these is a repeated scalar param on `/api/prospects` and its export and
 * all-matching siblings, shaped exactly like `gradYear`.
 */
const RESERVED_FILTER_PARAMS: Record<string, string> = {
  [GRAD_YEAR_FILTER_KEY]: "gradYear",
  [PLAYER_LEVEL_FILTER_KEY]: "playerLevel",
  [JUCO_YEAR_FILTER_KEY]: "jucoYear",
};

/**
 * Encode tag filters onto a query string.
 *
 * A tag name becomes `tag_<NameWithoutSpaces>`, repeated once per selected value:
 * "Game Impact" → `tag_GameImpact`. The server ORs within a category and ANDs across
 * categories.
 *
 * The reserved keys are not tags. Grad Year, Level and JC Year each map to a scalar
 * column on the prospect row via their own repeatable param, because those facts live on
 * the row rather than in the tag relation. Level carries the wire ordinal as a string
 * (`'0'` is High School and must never be dropped by a truthiness test); JC Year carries
 * `'1'`-`'3'`.
 *
 * Values are appended verbatim rather than validated here. The API answers an unknown
 * value with a 400 by design, so a mis-wired facet or a hand-edited URL fails loudly
 * instead of quietly widening the query to every prospect in the org.
 *
 * Shared by every endpoint that accepts these filters so the three call sites (list,
 * export, unpaginated read) cannot drift apart.
 */
const appendTagFilterParams = (
  queryParams: URLSearchParams,
  tagFilters: TagFilters | undefined
): void => {
  if (!tagFilters) return;

  for (const [tagName, values] of Object.entries(tagFilters)) {
    if (!values || values.length === 0) continue;

    const reservedParam = RESERVED_FILTER_PARAMS[tagName];
    if (reservedParam) {
      for (const value of values) {
        queryParams.append(reservedParam, value);
      }
      continue;
    }

    const paramName = `tag_${tagName.replace(/\s+/g, "")}`;
    for (const value of values) {
      queryParams.append(paramName, value);
    }
  }
};

export const getProspects = async (
  params: GetProspectsParams = {}
): Promise<PaginatedResponse<ProspectResponse>> => {
  const {
    teamId,
    pageNumber = 1,
    pageSize = 15,
    searchTerm,
    tagFilters,
    isFavorite,
    sortBy,
    sortDirection,
  } = params;

  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });

  if (teamId) {
    queryParams.append("teamId", teamId);
  }

  if (isFavorite !== undefined) {
    queryParams.append("isFavorite", String(isFavorite));
  }

  if (searchTerm) {
    queryParams.append("searchTerm", searchTerm);
  }

  if (sortBy) {
    queryParams.append("sortBy", sortBy);
    // Direction is meaningless without a column, and the server defaults to
    // ascending, so only send it alongside sortBy.
    queryParams.append("sortDirection", sortDirection ?? "asc");
  }

  appendTagFilterParams(queryParams, tagFilters);

  const response = await get<PaginatedResponse<ProspectResponse>>(
    `/api/prospects?${queryParams.toString()}`
  );

  return response;
};

export type GetAllMatchingProspectsParams = {
  teamId?: string;
  searchTerm?: string;
  tagFilters?: TagFilters;
};

/**
 * Fetch EVERY prospect matching the filters, unpaginated, in a lean row shape.
 *
 * Backs the call-list builder, which assigns coaches to a whole filtered set at once, so
 * a page-sized read would silently hide part of the list the coach thinks they are acting
 * on. The response reports `totalCount` and `isTruncated` separately from `items`, so a
 * caller can tell a complete set apart from a capped one and say so.
 */
export const getAllMatchingProspects = async (
  params: GetAllMatchingProspectsParams = {}
): Promise<FilteredProspectsResponse> => {
  const { teamId, searchTerm, tagFilters } = params;

  const queryParams = new URLSearchParams();

  if (teamId) {
    queryParams.append("teamId", teamId);
  }

  if (searchTerm) {
    queryParams.append("searchTerm", searchTerm);
  }

  appendTagFilterParams(queryParams, tagFilters);

  return get<FilteredProspectsResponse>(
    `/api/prospects/all-matching?${queryParams.toString()}`
  );
};

/**
 * Get the distinct graduation years present across the org's prospects,
 * most-recent-first. Used to populate the Grad Year filter options.
 */
export const getGradYears = async (): Promise<number[]> => {
  return get<number[]>(`/api/prospects/grad-years`);
};

/**
 * Get a single prospect by ID
 * Fetches complete prospect data including metrics
 */
export const getProspect = async (prospectId: string): Promise<ProspectResponse> => {
  return get<ProspectResponse>(`/api/prospects/${prospectId}`);
};

export const searchProspects = (
  searchTerm: string,
  take = 20,
  signal?: AbortSignal,
): Promise<PaginatedResponse<ProspectResponse>> => {
  const queryParams = new URLSearchParams({
    pageNumber: '1',
    pageSize: String(take),
    searchTerm,
  });
  return get<PaginatedResponse<ProspectResponse>>(
    `/api/prospects?${queryParams.toString()}`,
    { signal },
  );
};

export const createProspect = async (
  data: CreateProspectRequest
): Promise<ProspectResponse> => {
  return post<ProspectResponse>('/api/prospects', data);
};

export type ExportProspectsParams = {
  teamId?: string;
  searchTerm?: string;
  tagFilters?: TagFilters;
};

/**
 * Export prospects to CSV file
 * Returns a Blob that can be downloaded by the browser
 */
export const exportProspectsToCsv = async (
  params: ExportProspectsParams = {}
): Promise<Blob> => {
  const { teamId, searchTerm, tagFilters } = params;

  const queryParams = new URLSearchParams();

  if (teamId) {
    queryParams.append("teamId", teamId);
  }

  if (searchTerm) {
    queryParams.append("searchTerm", searchTerm);
  }

  appendTagFilterParams(queryParams, tagFilters);

  return await getBlob(`/api/prospects/export?${queryParams.toString()}`);
};

/** One export archive as the exports panel sees it. */
export interface ProspectExport {
  id: string;
  status: "Pending" | "Running" | "Completed" | "Failed";
  teamId?: string | null;
  teamName?: string | null;
  searchTerm?: string | null;
  gradYears?: string | null;
  prospectCount: number;
  pageCount: number;
  /** Pages the caps or a failed read left out. Non-zero means the archive is partial. */
  omittedPageCount: number;
  fileSizeBytes: number;
  errorMessage?: string | null;
  /** Whether asking for a link will succeed — finished, uploaded, unexpired. */
  isDownloadable: boolean;
  createdAt: string;
  completedAt?: string | null;
  expiresAt?: string | null;
}

/**
 * Ask for an export archive: the CSV plus every handwritten note page for the players the
 * filters cover.
 *
 * Returns as soon as the job is queued — building it reads every page out of cloud storage,
 * which is far longer than a request can stay open. The finished file shows up in the
 * exports panel.
 */
export const requestProspectExport = async (
  params: ExportProspectsParams = {}
): Promise<ProspectExport> => {
  const { teamId, searchTerm, tagFilters } = params;

  const queryParams = new URLSearchParams();

  if (teamId) {
    queryParams.append("teamId", teamId);
  }

  if (searchTerm) {
    queryParams.append("searchTerm", searchTerm);
  }

  appendTagFilterParams(queryParams, tagFilters);

  return await post<ProspectExport>(
    `/api/prospects/exports?${queryParams.toString()}`,
    undefined
  );
};

/** The current user's export archives, newest first. */
export const getProspectExports = async (): Promise<ProspectExport[]> =>
  await get<ProspectExport[]>("/api/prospects/exports");

/**
 * A short-lived signed link to a finished archive. Minted per request rather than stored,
 * so a link cannot outlive the person who should have had it.
 */
export const getProspectExportDownloadUrl = async (
  exportId: string
): Promise<{ url: string; expiresAt: string }> =>
  await get<{ url: string; expiresAt: string }>(
    `/api/prospects/exports/${exportId}/download`
  );

export type FavoriteToggleResponse = {
  message: string;
  prospectId: string;
  favoritedAt: string | null;
};

/**
 * Add prospect to favorites
 */
export const addToFavorites = async (
  prospectId: string
): Promise<FavoriteToggleResponse> => {
  return post<FavoriteToggleResponse>(
    `/api/prospects/${prospectId}/favorite`,
    {}
  );
};

/**
 * Remove prospect from favorites
 */
export const removeFromFavorites = async (
  prospectId: string
): Promise<FavoriteToggleResponse> => {
  return del<FavoriteToggleResponse>(`/api/prospects/${prospectId}/favorite`);
};

/**
 * Toggle prospect favorite status. Returns the server-authoritative
 * favoritedAt stamp (null when unfavorited) for optimistic reconciliation.
 */
export const toggleFavorite = async (
  prospectId: string,
  currentIsFavorite: boolean
): Promise<FavoriteToggleResponse> => {
  if (currentIsFavorite) {
    return removeFromFavorites(prospectId);
  } else {
    return addToFavorites(prospectId);
  }
};

/**
 * Get default tag definitions
 * Returns all available tag categories with their options
 */
export const getDefaultTagDefinitions = async (): Promise<DefaultTagDefinition[]> => {
  return get<DefaultTagDefinition[]>('/api/default-tags');
};

/**
 * Update prospect tags
 * Replaces all values for a specific tag category
 */
export const updateProspectTags = async (
  prospectId: string,
  tagName: string,
  values: string[]
): Promise<ProspectDefaultTag[]> => {
  return put<ProspectDefaultTag[]>(
    `/api/prospects/${prospectId}/default-tags`,
    { tagName, values }
  );
};

/**
 * Update position player metrics
 * Performs partial update of position player metrics
 */
export const updatePositionPlayerMetric = async (
  prospectId: string,
  metrics: PartialPositionPlayerMetrics
): Promise<ProspectResponse> => {
  const response = await patch<ProspectResponse>(
    `/api/prospects/${prospectId}`,
    { positionPlayerMetrics: metrics }
  );

  return response;
};

/**
 * Update pitcher metrics
 * Performs partial update of pitcher metrics
 */
export const updatePitcherMetric = async (
  prospectId: string,
  metrics: PartialPitcherMetrics
): Promise<ProspectResponse> => {
  return patch<ProspectResponse>(
    `/api/prospects/${prospectId}`,
    { pitcherMetrics: metrics }
  );
};

/**
 * The `nullFields` entries a profile save must carry to clear the level fields.
 *
 * A top-level `playerLevel: null` does not clear anything: the API reads null as "field not
 * sent", and `nullFields` is the only channel that clears a scalar
 * (`ProspectRepository.PatchProspectAsync`). Clearing a level appears to work today only via the
 * deprecated `about.playerLevel` route, which Phase 6 removes - so the entry has to be here.
 *
 * The `jucoYear` rule encodes the invariant that a JC year belongs to the JUCO ladder alone:
 *
 *  - an explicit non-JUCO level takes any stored JC year with it, so a prospect retyped from
 *    JUCO to High School does not keep a stale "JC 2" on the wire for lists and exports;
 *  - an *unset* level clears nothing, because a roster import names the JC year without ever
 *    naming a level, and clearing there would eat imported data.
 *
 * An older server ignores names it does not know, so both entries are safe to send before the
 * API side of this deploys.
 */
export function levelNullFields(playerLevel: number | null | undefined): string[] {
  const fields: string[] = [];
  if (playerLevel == null) {
    fields.push("playerLevel");
    return fields;
  }
  if (!isJucoPlayerLevel(playerLevel)) fields.push("jucoYear");
  return fields;
}

/**
 * Update prospect profile data
 * Performs partial update of prospect profile including basic info and about section
 */
export const updateProspectProfile = async (
  prospectId: string,
  data: UpdateProspectProfileData
): Promise<ProspectResponse> => {
  return patch<ProspectResponse>(`/api/prospects/${prospectId}`, data);
};

export type UpdateProspectProfileData = {
  firstName?: string;
  lastName?: string;
  position?: string;
  gradYear?: number;
  /** Year of junior college ("1".."3"). Only meaningful when `playerLevel` is JUCO. */
  jucoYear?: JucoYear | null;
  /**
   * Level ordinal: 0 High School, 1 JUCO, 2 Transfer Portal. See `constants/playerLevels` -
   * `0` is High School and is falsy, so build this from `!= null`, never from `||`.
   *
   * Callers that set this should also set `about.playerLevel` below until the old column is
   * dropped, so the save lands whichever side of the API deploy the request reaches.
   */
  playerLevel?: number | null;
  height?: string;
  weight?: number;
  bats?: string;
  throws?: string;
  phone?: string;
  email?: string;
  nullFields?: string[];
  about?: {
    twitterUrl?: string | null;
    instagramUrl?: string | null;
    playerLevel?: number | null;
    city?: string | null;
    state?: string | null;
    birthDate?: string | null;
    visitDate?: string | null;
    address?: string | null;
    zipCode?: string | null;
    parentName?: string | null;
    parentPhone?: string | null;
    coachName?: string | null;
    coachPhone?: string | null;
    advisorName?: string | null;
    advisorPhone?: string | null;
    /** Null clears it. Written from a recruiting board's player dialog on a linked row. */
    travelTeam?: string | null;
  };
};

/**
 * Get metric history entries for a prospect
 * Returns paginated list of historical metric values
 */
export const getMetricEntries = async (
  prospectId: string,
  params: GetMetricEntriesParams = {}
): Promise<PaginatedResponse<MetricEntryResponse>> => {
  const { metricName, metricCategory, pageSize = 10, pageNumber = 1 } = params;

  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });

  if (metricName) {
    queryParams.append('metricName', metricName);
  }

  if (metricCategory) {
    queryParams.append('metricCategory', metricCategory);
  }

  return get<PaginatedResponse<MetricEntryResponse>>(
    `/api/prospects/${prospectId}/metric-entries?${queryParams.toString()}`
  );
};

/**
 * Delete a prospect
 * Performs soft delete on the prospect
 */
export const deleteProspect = (prospectId: string) =>
  del<void>(`/api/prospects/${prospectId}`);

/**
 * Bulk delete prospects
 * Performs soft delete on up to 100 prospects at once
 */
export const bulkDeleteProspects = (prospectIds: string[]) =>
  del<{ deletedCount: number; notFoundIds: string[] }>('/api/prospects/bulk', {
    body: JSON.stringify({ prospectIds }),
  });

/**
 * Duplicate-import guard: check roster-import candidates against existing prospects
 * (name + grad year + position + team) BEFORE any records are created. Returns only
 * the flagged candidates, each with the existing prospect(s) it likely duplicates.
 */
export const checkImportDuplicates = async (
  request: CheckImportDuplicatesRequest
): Promise<CheckImportDuplicatesResponse> =>
  await post<CheckImportDuplicatesResponse>(
    "/api/prospects/check-import-duplicates",
    request
  );

/**
 * A typed merge failure. Carries the machine-readable {@link MergeErrorResponse}
 * body (code + conflictingIds) so the UI can branch on `code` and name the
 * implicated ids. Thrown by {@link mergeProspects} for any non-2xx response that
 * carries a recognizable merge error body; otherwise the raw {@link ApiError}
 * propagates.
 */
export class MergeError extends Error {
  readonly code: MergeErrorResponse["code"];
  readonly conflictingIds: string[];
  readonly statusCode: number;

  constructor(body: MergeErrorResponse, statusCode: number) {
    super(body.message || body.code);
    this.name = "MergeError";
    this.code = body.code;
    this.conflictingIds = body.conflictingIds ?? [];
    this.statusCode = statusCode;
  }
}

/**
 * Type guard for a well-formed {@link MergeErrorResponse} body. We only treat a
 * response as a typed merge error when it actually carries a `code` string —
 * anything else (HTML error pages, generic `{ message }` bodies) falls through
 * to the raw ApiError so we never mask an unexpected failure as a known one.
 */
const isMergeErrorBody = (value: unknown): value is MergeErrorResponse => {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { code?: unknown }).code === "string"
  );
};

/**
 * Merge one or more "loser" prospects into a single survivor.
 *
 * Generates a fresh `Idempotency-Key` per call. To safely retry the SAME merge
 * attempt (e.g. after a network blip) without risking a double-merge, pass the
 * key returned in {@link MergeAttempt.idempotencyKey} back in via `idempotencyKey`
 * so the server replays the original outcome instead of re-executing.
 *
 * On a recognized merge failure this rejects with a {@link MergeError} carrying
 * the typed `code` and `conflictingIds`; the caller branches on `code` to render
 * a specific message and must keep all profiles visible (no optimistic removal).
 */
export type MergeAttempt = {
  /** The key sent with the request. Re-pass it to retry the same attempt safely. */
  idempotencyKey: string;
  result: MergeProspectsResponse;
};

export const mergeProspects = async (
  request: MergeProspectsRequest,
  idempotencyKey: string = crypto.randomUUID()
): Promise<MergeAttempt> => {
  try {
    const result = await post<MergeProspectsResponse>(
      "/api/prospects/merge",
      request,
      { headers: { "Idempotency-Key": idempotencyKey } }
    );
    return { idempotencyKey, result };
  } catch (err) {
    if (err instanceof ApiError && isMergeErrorBody(err.response)) {
      throw new MergeError(err.response, err.statusCode);
    }
    throw err;
  }
};

/**
 * Build the `expectedVersions` optimistic-concurrency map for a merge from the
 * survivor + losers, keyed by prospect id → the `updatedAt` the client last saw.
 */
export const buildExpectedVersions = (
  prospects: Pick<ProspectResponse, "id" | "updatedAt">[]
): Record<string, string> => {
  const versions: Record<string, string> = {};
  for (const p of prospects) {
    versions[p.id] = p.updatedAt;
  }
  return versions;
};

// ---------------------------------------------------------------------------
// Merge Manager — org-wide duplicate detection
// ---------------------------------------------------------------------------

/**
 * Open duplicate-group count for the nav badge. Reads stored rows server-side —
 * cheap enough to call on every dashboard load.
 */
export const getDuplicateCount = async (): Promise<number> => {
  const response = await get<DuplicateCountResponse>("/api/prospects/duplicates/count");
  return response.count;
};

/** Paginated duplicate groups, strongest corroboration first. */
export const getDuplicateCandidates = (
  pageNumber = 1,
  pageSize = 20
): Promise<PaginatedResponse<DuplicateCandidate>> =>
  get<PaginatedResponse<DuplicateCandidate>>(
    `/api/prospects/duplicates?pageNumber=${pageNumber}&pageSize=${pageSize}`
  );

/**
 * Dismiss a group as "not the same player". Permanent and org-wide: the server
 * records every member pair, so a subset of this group stays hidden too.
 */
export const dismissDuplicateCandidate = (candidateId: string): Promise<void> =>
  post<void>(`/api/prospects/duplicates/${candidateId}/dismiss`, {});

/** Previously dismissed pairs, so a mistaken dismissal can be undone. */
export const getDismissedDuplicates = (): Promise<DismissedDuplicate[]> =>
  get<DismissedDuplicate[]>("/api/prospects/duplicates/dismissed");

export const restoreDismissedDuplicate = (dismissalId: string): Promise<void> =>
  del<void>(`/api/prospects/duplicates/dismissed/${dismissalId}`);

/** Force a full recompute for the organization. */
export const sweepDuplicates = (): Promise<DuplicateSweepResponse> =>
  post<DuplicateSweepResponse>("/api/prospects/duplicates/sweep", {});

/**
 * Merge several duplicate groups at once. Always resolves (never throws for a
 * partial run) — inspect `results` for the groups that failed.
 */
export const bulkMergeProspects = (request: BulkMergeRequest): Promise<BulkMergeResponse> =>
  post<BulkMergeResponse>("/api/prospects/merge/bulk", request);

/**
 * Merge every open duplicate group in the organization. Sends no ids: the server
 * walks the groups itself and returns a job to poll, because a run over thousands
 * of groups takes minutes and no request should sit on it.
 *
 * Idempotent while a run is open — calling it again returns the job in flight.
 */
export const startMergeAllDuplicates = (): Promise<MergeAllJob> =>
  post<MergeAllJob>("/api/prospects/duplicates/merge-all", {});

/**
 * Stop the organization's open run. The groups already merged stay merged — this
 * ends the run, it does not undo it. Returns null when there was nothing to stop.
 */
export const cancelMergeAllDuplicates = async (): Promise<MergeAllJob | null> => {
  const job = await post<MergeAllJob | null>(
    "/api/prospects/duplicates/merge-all/cancel",
    {}
  );
  return job && job.jobId ? job : null;
};

/**
 * The organization's most recent merge-all run, or null if it has never started
 * one. Read on mount so a tab opened mid-run shows the progress.
 */
export const getMergeAllJob = async (): Promise<MergeAllJob | null> => {
  // The endpoint answers 204 when the org has never run one, which the API client
  // surfaces as an empty object — so identify "no job" by the missing id.
  const job = await get<MergeAllJob | null>("/api/prospects/duplicates/merge-all/current");
  return job && job.jobId ? job : null;
};

/**
 * Get all prospects for a team without pagination.
 * Used exclusively for reorder mode.
 */
export const getAllProspectsForTeam = async (teamId: string): Promise<ProspectResponse[]> => {
  return get<ProspectResponse[]>(`/api/prospects/all-for-team?teamId=${teamId}`);
};

/**
 * Save a custom sort order for prospects within a team.
 * @param teamId - The team whose order is being saved
 * @param orderedIds - Prospect IDs in the desired display order
 */
export const reorderProspects = async (teamId: string, orderedIds: string[]): Promise<void> => {
  return patch<void>('/api/prospects/reorder', { teamId, orderedProspectIds: orderedIds });
};

/**
 * Get merged tag definitions for the authenticated user's organization.
 * Returns defaults + any org-specific customizations.
 */
export const getOrgTagDefinitions = async (): Promise<DefaultTagDefinition[]> => {
  return get<DefaultTagDefinition[]>('/api/tag-definitions');
};

/**
 * The tag categories the signed-in user's mobile prospect list offers as quick-tag pills, in order.
 *
 * Per user, not per organization — always "mine". There is no endpoint for reading or setting
 * somebody else's, so no coach can change another's phone.
 *
 * `tagNames: null` means never configured — mobile then falls back to its historical single
 * Interest pill. An empty array means the user chose to show none. The two are not the same.
 */
export type MobileQuickTags = {
  tagNames: string[] | null;
  maxCount: number;
};

export const getMobileQuickTags = async (): Promise<MobileQuickTags> => {
  return get<MobileQuickTags>('/api/tag-definitions/mobile-quick-tags');
};

/** Replaces the signed-in user's whole selection, in pill order. An empty array shows none. */
export const updateMobileQuickTags = async (
  tagNames: string[]
): Promise<MobileQuickTags> => {
  return put<MobileQuickTags>('/api/tag-definitions/mobile-quick-tags', { tagNames });
};

/**
 * Create a new custom tag category for the organization.
 */
export const createTagDefinition = async (
  request: CreateTagDefinitionRequest
): Promise<DefaultTagDefinition> => {
  return post<DefaultTagDefinition>('/api/tag-definitions', request);
};

/**
 * Update the options for an existing tag category.
 * Works for both default and custom categories.
 */
export const updateTagDefinition = async (
  tagName: string,
  options: string[]
): Promise<DefaultTagDefinition> => {
  return put<DefaultTagDefinition>(`/api/tag-definitions/${encodeURIComponent(tagName)}`, { options });
};

/**
 * Delete a tag category for the organization.
 * Works for both default and custom categories.
 * Existing prospect values for this category are preserved.
 */
export const deleteTagDefinition = async (tagName: string): Promise<void> => {
  return del<void>(`/api/tag-definitions/${encodeURIComponent(tagName)}`);
};
